const API = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const USER_TOKEN_KEY = 'pointspilot_user_token';
const USER_META_KEY = 'pointspilot_beta_consent_v2_meta';

export function getOrCreateUserToken() {
  try {
    let token = localStorage.getItem(USER_TOKEN_KEY);
    if (!token) {
      token = (crypto.randomUUID && crypto.randomUUID()) ||
              Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem(USER_TOKEN_KEY, token);
    }
    return token;
  } catch {
    return null;
  }
}

function readMeta() {
  try { return JSON.parse(localStorage.getItem(USER_META_KEY) || '{}'); } catch { return {}; }
}

function buildHeaders(extra = {}) {
  const headers = { 'Content-Type': 'application/json', ...extra };
  const token = getOrCreateUserToken();
  if (token) headers['X-User-Token'] = token;
  const meta = readMeta();
  if (meta.name) headers['X-User-Name'] = meta.name;
  if (meta.email) headers['X-User-Email'] = meta.email;
  return headers;
}

async function request(path, options = {}) {
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: buildHeaders(options.headers),
    signal: options.signal,
  });
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

export const api = {
  getTrip: () => request('/trip'),
  updateTrip: (data) => request('/trip', { method: 'PATCH', body: JSON.stringify(data) }),
  // Streaming version — backend sends NDJSON progress events line by
  // line, then a final {done:true, plan} line. Consumer passes
  // onProgress(event) to update the UI in real time. 4-min timeout —
  // but the stream itself keeps the connection alive, so this only fires
  // if the server truly stops responding.
  autoPlan: async (data, onProgress) => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 240000);
    try {
      const res = await fetch(`${API}/auto-plan`, {
        method: 'POST',
        headers: buildHeaders({ 'Content-Type': 'application/json', 'Accept': 'application/x-ndjson' }),
        body: JSON.stringify(data),
        signal: ctrl.signal,
      });
      if (!res.ok) throw new Error(`API error: ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let finalPayload = null;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // keep partial
        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const evt = JSON.parse(line);
            if (evt.done) {
              finalPayload = evt;
            } else if (onProgress) {
              onProgress(evt);
            }
          } catch (parseErr) {
            // Ignore unparseable lines — could be heartbeat fragments
          }
        }
      }
      if (!finalPayload) throw new Error('Stream ended without final payload');
      return finalPayload;
    } finally {
      clearTimeout(timer);
    }
  },
  autoPlanChat: (data) => request('/auto-plan/chat', { method: 'POST', body: JSON.stringify(data) }),
  getSeatsAeroTripDetail: (id) => request(`/seats-aero/trip-detail/${encodeURIComponent(id)}`),
  compareCabins: (origin, destination, startDate, endDate) =>
    request(`/seats-aero/compare-cabins?origin=${origin}&destination=${destination}&startDate=${startDate || ''}&endDate=${endDate || ''}`),
  awardsByDate: (origin, destination, cabin, startDate, endDate) =>
    request(`/seats-aero/by-date?origin=${origin}&destination=${destination}&cabin=${cabin || 'business'}&startDate=${startDate || ''}&endDate=${endDate || ''}`),
  getPilotChatHistory: (destinationId) => request(`/auto-plan/chat-history?destinationId=${destinationId}`),
  savePilotChatHistory: (data) => request('/auto-plan/chat-history', { method: 'POST', body: JSON.stringify(data) }),

  // Award alerts — watch a route, get notified when miles drop
  getAwardAlerts: () => request('/award-alerts'),
  createAwardAlert: (data) => request('/award-alerts', { method: 'POST', body: JSON.stringify(data) }),
  deleteAwardAlert: (id) => request(`/award-alerts/${id}`, { method: 'DELETE' }),
  checkAwardAlert: (id) => request(`/award-alerts/${id}/check`, { method: 'POST' }),

  // Trip memory — preferences carry forward across trips
  getTripMemory: () => request('/trip-memory'),
  saveTripMemory: (data) => request('/trip-memory', { method: 'POST', body: JSON.stringify(data) }),

  // Companion comments on shared trips
  getCompanionComments: (token) => request(`/trip/shared/${token}/comments`),
  postCompanionComment: (token, data) => request(`/trip/shared/${token}/comments`, { method: 'POST', body: JSON.stringify(data) }),

  // Trip versions — save plan snapshots + compare side-by-side
  getTripVersions: (destinationId) => request(`/trip-versions${destinationId ? `?destinationId=${destinationId}` : ''}`),
  saveTripVersion: (data) => request('/trip-versions', { method: 'POST', body: JSON.stringify(data) }),
  deleteTripVersion: (id) => request(`/trip-versions/${id}`, { method: 'DELETE' }),

  // Per-pick personal notes
  getTripNotes: (destinationId) => request(`/trip-notes?destinationId=${destinationId}`),
  saveTripNote: (data) => request('/trip-notes', { method: 'POST', body: JSON.stringify(data) }),

  // Inspiration — "where can I go with my points"
  getInspiration: (data) => request('/inspiration', { method: 'POST', body: JSON.stringify(data) }),
  // AI Suggest — get fresh AI+web-search-backed picks for any city/category
  aiSuggest: (data) => request('/ai-suggest', { method: 'POST', body: JSON.stringify(data) }),

  // Card portfolio + credit tracking
  getCardCatalog: () => request('/card-catalog'),
  getMyCards: () => request('/my-cards'),
  addMyCard: (data) => request('/my-cards', { method: 'POST', body: JSON.stringify(data) }),
  removeMyCard: (cardKey) => request(`/my-cards/${cardKey}`, { method: 'DELETE' }),
  logCreditUsage: (data) => request('/credit-usage', { method: 'POST', body: JSON.stringify(data) }),

  getDestinations: () => request('/destinations'),
  getDestination: (id) => request(`/destinations/${id}`),
  toggleFavorite: (id) => request(`/destinations/${id}/favorite`, { method: 'PATCH' }),
  selectDestination: (id) => request(`/destinations/${id}/select`, { method: 'POST' }),

  getRedemptions: (destId) => request(`/redemptions/${destId}`),
  getHotels: (destId) => request(`/hotels/${destId}`),
  getActivities: (destId) => request(`/activities/${destId}`),
  getRestaurants: (destId) => request(`/restaurants/${destId}`),

  getTransferPartners: (source) => request(`/transfer-partners${source ? `?source=${source}` : ''}`),
  getTransferBonuses: () => request('/transfer-bonuses'),

  getSweetSpots: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/sweet-spots${qs ? `?${qs}` : ''}`);
  },

  getChecklist: () => request('/checklist'),
  updateChecklist: (id, is_completed) => request(`/checklist/${id}`, { method: 'PATCH', body: JSON.stringify({ is_completed }) }),

  // Trip selections (shopping cart)
  getSelections: () => request('/selections'),
  addSelection: (data) => request('/selections', { method: 'POST', body: JSON.stringify(data) }),
  removeSelection: (id) => request(`/selections/${id}`, { method: 'DELETE' }),
  getBudgetSummary: () => request('/budget-summary'),

  sendAdvisorMessage: (messages) => request('/advisor', { method: 'POST', body: JSON.stringify({ messages }) }),

  // Live award availability (seats.aero)
  searchLiveAvailability: (origin, destination, cabin = 'business', startDate, endDate) =>
    request(`/live-availability/multi?origin=${origin}&destination=${destination}&cabin=${cabin}${startDate ? `&startDate=${startDate}` : ''}${endDate ? `&endDate=${endDate}` : ''}`),
  getLiveTrip: (id) => request(`/live-availability/trips/${id}`),

  // Live hotel availability
  searchLiveHotels: (destination, checkin, checkout, source) =>
    request(`/live-hotels?destination=${destination}${checkin ? `&checkin=${checkin}` : ''}${checkout ? `&checkout=${checkout}` : ''}${source ? `&source=${source}` : ''}`),

  // Availability alerts
  createAlert: (data) => request('/alerts', { method: 'POST', body: JSON.stringify(data) }),
  getAlerts: () => request('/alerts'),
  deleteAlert: (id) => request(`/alerts/${id}`, { method: 'DELETE' }),
  checkAlerts: () => request('/alerts/check', { method: 'POST' }),
  getAlertResults: (id) => request(`/alerts/${id}/results`),

  // Share trip
  getShareData: () => request('/trip/share'),
  createShareLink: () => request('/trip/share-link', { method: 'POST' }),
  getSharedTrip: (token) => request(`/trip/shared/${token}`),

  // Calendar export URL (opened directly, not fetched as JSON)
  calendarExportUrl: () => `${API}/trip/calendar`,
};
